import { MbeInfo } from '@/components/icons';
import { couponCodeRegex } from '@/constants';
import { Uploader } from '@/providers';
import type { IBaseForm, IItem, IFile } from '@/types';
import { hasEmptyFields, hasFormAnyError } from '@/utils';
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Progress,
  Switch,
  Tooltip,
  useBoolean,
  useMediaQuery,
} from '@chakra-ui/react';
import type { ChangeEvent } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ERRORS, hasValidFormat, hasValidSize } from '../../constants';
import { useCheckCoupon } from '../../hooks';
import type { IAuctionHouseInfo } from '../../types';
import { isCategoryWithFragility } from '../../utils';
import { FileUploadInput, MbeInput } from '../shared';
import { AuctionHouseInfo } from './AuctionHouseInfo';
import { FileUploadedList } from './FileUploadedList';
import { ItemInfo } from './ItemInfo';

const requiredFields = ['name', 'streetAddress', 'city', 'countryCode', 'zipCode', 'batchValue'];

export const AuctionHouseForm: React.FC<IBaseForm<IAuctionHouseInfo>> = ({
  defaultValues,
  isBackVisible = true,
  onBack,
  onSubmit: providedOnSubmit,
}): JSX.Element => {
  const { t } = useTranslation();
  const [isUploading, setUploading] = useBoolean(false);
  const isValidCoupon = useCheckCoupon();
  const methods = useForm<IAuctionHouseInfo>({ defaultValues, mode: 'onChange' });
  const [isLargerThan768] = useMediaQuery('(min-width: 48em)');

  const {
    clearErrors,
    control,
    formState: { errors },
    getValues,
    handleSubmit: submitHandler,
    register,
    resetField,
    setError,
    setValue,
    watch,
  } = methods;

  const couponInputProps = register('couponCode', {
    pattern: { value: couponCodeRegex, message: t(ERRORS.invalidCoupon) },
    validate: {
      isValid: async (coupon) => {
        if (!coupon) return;

        const message = t(ERRORS.invalidCoupon);
        const isValid = await isValidCoupon(coupon);
        return isValid || message;
      },
    },
  });

  const { field: myFileField } = useController({
    defaultValue: [],
    name: 'myFile',
    control,
  });

  const uploadedFiles = watch('myFile');
  const draftItem = watch('item');

  const [areItemDtsVisible, setPrdDtsVisibility] = useBoolean(
    Object.keys(defaultValues?.item ?? {}).length > 0
  );

  const [isCouponCodeVisible, setCouponCodeVisibility] = useBoolean(
    Boolean(defaultValues?.couponCode)
  );

  const areRequiredItemFieldsEmpty = areItemDtsVisible
    ? hasEmptyFields<IItem | object, Extract<keyof IItem, never>>(draftItem ?? {}, [
        'category',
        'height',
        'length',
        'name',
        'weight',
        'width',
      ] as unknown as Extract<keyof IItem, never>[])
    : false;

  const hasFragility = isCategoryWithFragility(draftItem?.category ?? '')
    ? !areRequiredItemFieldsEmpty && draftItem?.fragility !== undefined
    : !areRequiredItemFieldsEmpty;

  const hasMissingItemProps = !hasFragility;

  const areRequiredFieldsEmpty = hasEmptyFields<
    IAuctionHouseInfo,
    Extract<keyof IAuctionHouseInfo, string>
  >(watch(), requiredFields as unknown as Extract<keyof IAuctionHouseInfo, string>[]);

  const isDisabled = hasMissingItemProps || areRequiredFieldsEmpty || hasFormAnyError(errors);

  const handleSubmit: SubmitHandler<IAuctionHouseInfo> = (data, event): void => {
    event?.preventDefault();
    providedOnSubmit?.({ ...data, hasItemData: areItemDtsVisible });
  };

  const handleBack = (): void => {
    onBack?.();
  };

  const handleDeleteFileUploaded = (file: IFile): void => {
    const files = getValues('myFile');
    // eslint-disable-next-line unicorn/prefer-spread
    const filteredFiles = Array.from(files).filter((f) => f.name !== file.name);
    setValue('myFile', filteredFiles, { shouldValidate: true });
  };

  const handleUploadChange = (e: ChangeEvent<HTMLInputElement>): void => {
    // eslint-disable-next-line unicorn/prefer-spread
    const files = Array.from(e.currentTarget.files ?? []);

    const hasInvalidSize = !files.every(hasValidSize);
    const hasInvalidFormat = !files.every(hasValidFormat);

    if (hasInvalidSize)
      setError('myFile', { type: 'fileSize', message: t(ERRORS.invalidFileSize) });
    else if (hasInvalidFormat)
      setError('myFile', { type: 'fileType', message: t(ERRORS.invalidFileType) });

    if (!hasInvalidSize && !hasInvalidFormat) {
      clearErrors('myFile');

      if (files.length > 0) {
        setUploading.on();

        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        const cb = ({ name, size, type }: File, id: string): void => {
          const uploaded = {
            id,
            name,
            type,
            size,
          } as IFile;

          setUploading.off();
          myFileField.onChange([uploaded]);
        };

        void Uploader.uploadFile(files[0], cb);
      }
    }

    e.target.value = '';
  };

  return (
    <FormProvider {...methods}>
      <form
        autoComplete="off"
        id="auctionhouseForm"
        name="auctionhouseForm"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={submitHandler(handleSubmit)}
      >
        <Flex direction="column" gap={12} mb={8}>
          <AuctionHouseInfo />
          <FormControl alignItems="center" display="flex" justifyContent="space-between">
            <FormLabel htmlFor="itemDetails" mb="0">
              {t('FORM.ITEM_DETAILS')}
            </FormLabel>
            <Switch
              colorScheme="red"
              id="itemDetails"
              isChecked={areItemDtsVisible}
              size="lg"
              onChange={(): void => {
                if (areItemDtsVisible)
                  resetField('item', {
                    keepError: false,
                    defaultValue: {
                      category: undefined,
                      fragility: undefined,
                      height: undefined,
                      length: undefined,
                      name: undefined,
                      weight: undefined,
                      width: undefined,
                    },
                  });

                setPrdDtsVisibility.toggle();
              }}
            />
          </FormControl>
          {areItemDtsVisible && <ItemInfo />}
          <FormControl alignItems="center" display="flex" justifyContent="flex-start">
            <FormLabel htmlFor="showDiscountCode" mb="0">
              {t('FORM.COUPON_CODE_LABEL')}

              {isLargerThan768 && (
                <Tooltip bgColor="brand.black" label={t('FORM.COUPON_CODE_TOOLTIP')} p={4}>
                  <MbeInfo
                    _hover={{ color: 'white', bg: 'brand.red.light' }}
                    color="brand.red.light"
                    fontSize="h4"
                    ml={2}
                    rounded="full"
                  />
                </Tooltip>
              )}
            </FormLabel>
            <Switch
              colorScheme="red"
              flex={2}
              id="showDiscountCode"
              isChecked={isCouponCodeVisible}
              size="lg"
              textAlign="right"
              onChange={(): void => {
                if (isCouponCodeVisible)
                  resetField('couponCode', { keepError: false, defaultValue: undefined });

                setCouponCodeVisibility.toggle();
              }}
            />
          </FormControl>
          {isCouponCodeVisible && (
            <MbeInput
              error={errors.couponCode}
              id="couponCode"
              placeholder={t('FORM.COUPON_CODE')}
              {...couponInputProps}
            />
          )}
          {isUploading ? (
            <Progress colorScheme="red" isIndeterminate size="xs" />
          ) : (
            <>
              <FileUploadedList files={uploadedFiles} onDelete={handleDeleteFileUploaded} />
              <FileUploadInput
                error={errors.myFile}
                id="myFile"
                name={myFileField.name}
                ref={myFileField.ref}
                onChange={handleUploadChange}
              />
            </>
          )}
        </Flex>
        <Flex flexDirection="row-reverse" gap={2} justifyContent="space-between">
          <Button disabled={isDisabled} type="submit" variant="mbePrimary">
            {t('ACTIONS.NEXT')}
          </Button>
          {isBackVisible && (
            <Button color="brand.red.light" variant="link" onClick={handleBack}>
              {t('ACTIONS.PREV')}
            </Button>
          )}
        </Flex>
      </form>
    </FormProvider>
  );
};
